import { Link, useNavigate } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { BrainCircuit, SlidersHorizontal, LogOut, User, ShieldCheck, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { billingStatus } from "@/lib/api";

const AccountMenu = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [sub, setSub] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (user) billingStatus().then(setSub).catch(() => setSub(null));
    else setSub(null);
  }, [user]);

  if (user === undefined) {
    return <span className="w-8 h-8 rounded-full border border-[var(--line)] animate-pulse" data-testid="nav-account-loading" />;
  }

  if (!user) {
    return (
      <Link
        to="/login"
        data-testid="nav-signin"
        className="px-3 sm:px-4 py-1.5 rounded-md border border-neon-cyan/40 text-neon-cyan text-xs sm:text-sm hover:bg-neon-cyan hover:text-[#05060a] hover:glow-cyan transition-colors transition-shadow duration-200"
      >
        Sign in
      </Link>
    );
  }

  const initial = (user.name || user.email || "?").trim().charAt(0).toUpperCase();
  const isPro = !!sub?.has_access;
  const isAdmin = !!user?.is_admin;
  const planLabel = isAdmin ? "CoreText Owner · Unlimited" : isPro ? "CoreText Pro" : "CoreText Free";

  return (
    <div className="flex items-center gap-1.5 sm:gap-2">
      {sub !== null && !isPro && !isAdmin && (
        <Link
          to="/pro"
          data-testid="nav-upgrade-pro"
          className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-md border border-neon-cyan/40 bg-neon-cyan/10 text-neon-cyan text-xs sm:text-sm font-semibold hover:bg-neon-cyan hover:text-[#05060a] hover:glow-cyan transition-colors transition-shadow duration-200"
        >
          <Sparkles size={14} /> <span>Upgrade</span>
        </Link>
      )}
      <div className="relative" ref={ref}>
        <button
          data-testid="nav-account-button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 rounded-full border border-neon-cyan/40 pl-1 pr-1 py-1 hover:glow-cyan transition-shadow duration-200"
          aria-label="Account"
        >
          {user.picture ? (
            <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
          ) : (
            <span className="w-7 h-7 rounded-full bg-neon-cyan/15 text-neon-cyan font-display font-bold text-sm flex items-center justify-center">
              {initial}
            </span>
          )}
        </button>
        {open && (
          <div
            data-testid="nav-account-menu"
            className="absolute right-0 mt-2 w-56 glass neon-border rounded-xl p-2 z-50"
          >
            <div className="px-3 py-2 border-b border-[var(--line)] mb-1">
              <p className="font-display font-bold text-sm text-white truncate">{user.name || "Account"}</p>
              <p className="font-mono text-[11px] text-[var(--muted)] truncate">{user.email}</p>
            </div>
            <Link
              to="/pro"
              onClick={() => setOpen(false)}
              data-testid="menu-plan-label"
              className="flex items-center gap-2 px-3 py-2 rounded-md font-mono text-sm text-neon-cyan hover:bg-neon-cyan/[0.08] transition-colors"
            >
              <Sparkles size={15} /> {planLabel}
            </Link>
            <Link
              to="/history"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-3 py-2 rounded-md font-mono text-sm text-[var(--muted)] hover:text-white hover:bg-neon-cyan/[0.06] transition-colors"
            >
              <User size={15} /> My history
            </Link>
            <Link
              to="/intelligence"
              onClick={() => setOpen(false)}
              data-testid="nav-intelligence"
              className="flex items-center gap-2 px-3 py-2 rounded-md font-mono text-sm text-[var(--muted)] hover:text-white hover:bg-neon-cyan/[0.06] transition-colors"
            >
              <BrainCircuit size={15} /> Build Intelligence
            </Link>
            {user.is_admin && (
              <Link
                to="/admin"
                onClick={() => setOpen(false)}
                data-testid="nav-admin"
                className="flex items-center gap-2 px-3 py-2 rounded-md font-mono text-sm text-neon-amber hover:bg-neon-amber/[0.08] transition-colors"
              >
                <ShieldCheck size={15} /> Admin dashboard
              </Link>
            )}
            <button
              data-testid="nav-logout"
              onClick={async () => {
                setOpen(false);
                await logout();
                navigate("/");
              }}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-md font-mono text-sm text-[var(--muted)] hover:text-neon-red hover:bg-neon-red/[0.06] transition-colors"
            >
              <LogOut size={15} /> Sign out
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export const Nav = () => (
  <header className="fixed top-0 inset-x-0 z-40 glass border-b border-[var(--line)]">
    <div className="max-w-7xl mx-auto px-4 md:px-10 h-16 flex items-center justify-between gap-2">
      <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 sm:gap-2.5 group min-w-0">
        <span className="relative inline-flex items-center justify-center w-9 h-9 rounded-lg border border-neon-cyan/50 glow-cyan shrink-0">
          <BrainCircuit size={20} className="text-neon-cyan" strokeWidth={2} />
        </span>
        <div className="leading-none min-w-0">
          <span className="font-display font-bold text-base sm:text-lg tracking-[0.16em] sm:tracking-[0.18em] text-white text-glow-cyan">
            Coretext
          </span>
          <span className="hidden sm:block font-mono text-[9px] tracking-[0.34em] text-neon-cyan/70 mt-0.5">
            BUILD INTELLIGENCE
          </span>
        </div>
      </Link>
      <nav className="flex items-center gap-1.5 sm:gap-2 font-mono text-sm shrink-0">
        <Link
          to="/"
          data-testid="nav-audit"
          className="px-2.5 sm:px-3 py-1.5 rounded-md text-[var(--muted)] hover:text-white transition-colors duration-200"
        >
          Audit
        </Link>
        <Link
          to="/history"
          data-testid="nav-history"
          className="px-2.5 sm:px-3 py-1.5 rounded-md text-[var(--muted)] hover:text-white transition-colors duration-200"
        >
          History
        </Link>
        <Link
          to="/settings"
          data-testid="nav-settings"
          aria-label="Settings"
          className="p-2 rounded-md text-[var(--muted)] hover:text-neon-cyan hover:border-neon-cyan/40 border border-transparent transition-colors duration-200"
        >
          <SlidersHorizontal size={17} />
        </Link>
        <AccountMenu />
      </nav>
    </div>
  </header>
);

export const Footer = () => (
  <footer className="border-t border-[var(--line)] mt-24 relative">
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-8 font-mono text-xs text-[var(--muted)] flex flex-col md:flex-row justify-between gap-2">
      <span className="text-neon-cyan/70">CORETEXT // BUILD INTELLIGENCE LAYER</span>
      <span>Audit before you send. Stop paying to watch AI guess.</span>
    </div>
  </footer>
);
